"use client";

import React from "react";
import { Phone, Mail, MapPin, Clock, ShieldCheck, ArrowUpRight, ChevronRight, ExternalLink, Instagram, Facebook, Linkedin } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
function RecursiveMenuLinks({
  items,
  depth = 0
}: {
  items: MenuItem[];
  depth?: number;
}) {
  return <ul className={depth === 0 ? "space-y-2.5" : "mt-2 space-y-2 pl-3 border-l border-border/40"}>
      {items.map((item, i) => <li key={item.id || `menu-item-${i}`} data-index={i} className="text-sm">
          <Link to={item.href || "#"} className="group inline-flex items-center text-muted-foreground hover:text-primary transition-colors duration-150">
            <ChevronRight className="size-3 mr-1.5 opacity-60 group-hover:translate-x-0.5 group-hover:text-primary transition-transform duration-150" />
            <span>{item.label}</span>
          </Link>
          {item.children && item.children.length > 0 ? <RecursiveMenuLinks items={item.children} depth={depth + 1} /> : null}
        </li>)}
    </ul>;
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="space-y-3">
        <div className="h-4 w-24 bg-muted/40 animate-pulse rounded-sm" />
        <div className="h-4 w-32 bg-muted/40 animate-pulse rounded-sm" />
        <div className="h-4 w-28 bg-muted/40 animate-pulse rounded-sm" />
        <div className="h-4 w-36 bg-muted/40 animate-pulse rounded-sm" />
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return <ul className="space-y-2.5 text-sm text-muted-foreground">
        <li>
          <Link to="/HomePage" className="hover:text-primary transition-colors duration-150">
            Home
          </Link>
        </li>
        <li>
          <Link to="/AboutBmfPage" className="hover:text-primary transition-colors duration-150">
            About BMF
          </Link>
        </li>
        <li>
          <Link to="/ProjectsCaseStudiesPage" className="hover:text-primary transition-colors duration-150">
            Projects & Case Studies
          </Link>
        </li>
        <li>
          <Link to="/HealthSafetyAccreditationsPage" className="hover:text-primary transition-colors duration-150">
            Health & Safety
          </Link>
        </li>
        <li>
          <Link to="/AreasWeCoverPage" className="hover:text-primary transition-colors duration-150">
            Areas We Cover
          </Link>
        </li>
      </ul>;
  }
  return <RecursiveMenuLinks items={menuItems} />;
}
const serviceLinks = [{
  label: "Commercial Scaffolding",
  to: "/CommercialScaffoldingPage"
}, {
  label: "Residential Scaffolding",
  to: "/ResidentialScaffoldingPage"
}, {
  label: "Roofing Scaffolding",
  to: "/RoofingScaffoldingPage"
}, {
  label: "Solar Scaffolding",
  to: "/SolarScaffoldingPage"
}, {
  label: "Temporary Roofs",
  to: "/TemporaryRoofsPage"
}, {
  label: "New Build Scaffolding",
  to: "/NewBuildScaffoldingPage"
}, {
  label: "Refurbishment Scaffolding",
  to: "/RefurbishmentScaffoldingPage"
}, {
  label: "Industrial Scaffolding",
  to: "/IndustrialScaffoldingPage"
}, {
  label: "Specialist Access Scaffolding",
  to: "/SpecialistAccessScaffoldingPage"
}, {
  label: "Edge Protection",
  to: "/EdgeProtectionPage"
}, {
  label: "Chimney Scaffolding",
  to: "/ChimneyScaffoldingPage"
}, {
  label: "Contact / Request a Quote",
  to: "/ContactRequestQuotePage"
}];
const whoWeWorkWithLinks = [{
  label: "Roofing Contractors",
  to: "/RoofingContractorsPage"
}, {
  label: "Solar Installers",
  to: "/SolarInstallersPage"
}, {
  label: "Main Contractors & Developers",
  to: "/MainContractorsDevelopersPage"
}, {
  label: "Councils & Public Sector",
  to: "/CouncilsAndPublicSectorPage"
}, {
  label: "Property & Facilities Management",
  to: "/PropertyFacilitiesManagementPage"
}];
const areaLinks = [{
  label: "Berkshire",
  to: "/AreasWeCoverPage"
}, {
  label: "Surrey",
  to: "/AreasWeCoverPage"
}, {
  label: "Hampshire",
  to: "/AreasWeCoverPage"
}, {
  label: "Buckinghamshire",
  to: "/AreasWeCoverPage"
}, {
  label: "Greater London",
  to: "/AreasWeCoverPage"
}, {
  label: "View All Areas",
  to: "/AreasWeCoverPage"
}];
const accreditations = [{
  name: "NASC",
  label: "National Access & Scaffolding Confederation"
}, {
  name: "CHAS",
  label: "Contractors Health and Safety Assessment Scheme"
}];
export default function Footer() {
  const currentYear = new Date().getFullYear();
  return <section id="footer" data-nav="dark" className="dark bg-background text-foreground border-t border-border">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8 pt-16 pb-12">
        {/* Structural Pre-Footer Header Banner */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 pb-12 border-b border-border/60 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 mb-3">
              <span className="inline-block h-2 w-2 rounded-full bg-primary" />
              <p className="text-xs font-bold uppercase tracking-widest text-primary">
                Operational Scaffolding Contractor
              </p>
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-default">
              Safety-led scaffolding across London & the South East
            </h2>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-2xl">
              Serving main contractors, commercial developers, roofing contractors, solar installers, and domestic clients with verified engineering standards.
            </p>
          </div>

          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end justify-center">
            <Button asChild className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6 py-2.5 h-auto rounded-md shadow-xs transition-colors duration-150">
              <Link to="/ContactRequestQuotePage">
                Request a quote
                <ArrowUpRight className="size-4 ml-1" />
              </Link>
            </Button>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="size-4 text-primary shrink-0" />
              <span>NASC & CHAS verified</span>
            </div>
          </div>
        </div>

        {/* Main Footer Links & Information Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-12 gap-8 py-12">
          {/* Column 1: Brand, Legacy & Overview (2 Cols) */}
          <div className="lg:col-span-2 space-y-4">
            <Link to="/" className="inline-block focus:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-sm">
              <WvcLogo className="h-7 w-auto" />
            </Link>
            
            <p className="text-sm leading-relaxed text-muted-foreground pr-2">
              BMF Scaffolding Ltd delivers safety-led scaffolding and experienced site teams across the Home Counties and wider South East.
            </p>

            <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t border-border/40">
              <p className="font-semibold text-foreground uppercase tracking-wider text-[11px]">
                Registered Office
              </p>
              <p className="font-medium text-foreground">BMF SCAFFOLDING LTD</p>
              <p>Company number: 16391608</p>
              <p className="leading-relaxed">
                Registered office: 119 Barkham Ride, Wokingham, Berkshire, England, RG40 4EP
              </p>
            </div>

            <div className="pt-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-foreground mb-2.5">
                Verified Accreditations
              </p>
              <div className="flex flex-wrap gap-2">
                {accreditations.map((acc, i) => <Badge key={acc.name} data-index={i} variant="outline" className="bg-card/40 border-border text-foreground text-xs py-1 px-2.5 font-mono">
                    {acc.name}
                  </Badge>)}
              </div>
            </div>
          </div>

          {/* Column 2: Scaffolding Services (2 Cols) */}
          <div className="lg:col-span-2 space-y-3.5">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground font-default border-b border-border/40 pb-2">
              Core Services
            </h3>
            <ul className="space-y-2.5 text-sm">
              {serviceLinks.map((service, i) => <li key={service.label} data-index={i}>
                  <Link to={service.to} className="group inline-flex items-center text-muted-foreground hover:text-primary transition-colors duration-150">
                    <ChevronRight className="size-3 mr-1.5 opacity-60 group-hover:translate-x-0.5 group-hover:text-primary transition-transform duration-150" />
                    <span>{service.label}</span>
                  </Link>
                </li>)}
            </ul>
          </div>

          {/* Column 3: Who We Work With (2 Cols) */}
          <div className="lg:col-span-2 space-y-3.5">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground font-default border-b border-border/40 pb-2">
              Who We Work With
            </h3>
            <ul className="space-y-2.5 text-sm">
              {whoWeWorkWithLinks.map((sector, i) => <li key={sector.label} data-index={i}>
                  <Link to={sector.to} className="group inline-flex items-center text-muted-foreground hover:text-primary transition-colors duration-150">
                    <ChevronRight className="size-3 mr-1.5 opacity-60 group-hover:translate-x-0.5 group-hover:text-primary transition-transform duration-150" />
                    <span>{sector.label}</span>
                  </Link>
                </li>)}
            </ul>
          </div>

          {/* Column 4: Areas Covered (2 Cols) */}
          <div className="lg:col-span-2 space-y-3.5">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground font-default border-b border-border/40 pb-2">
              Areas
            </h3>
            <ul className="space-y-2.5 text-sm">
              {areaLinks.map((area, i) => <li key={area.label} data-index={i}>
                  <Link to={area.to} className="group inline-flex items-center text-muted-foreground hover:text-primary transition-colors duration-150">
                    <ChevronRight className="size-3 mr-1.5 opacity-60 group-hover:translate-x-0.5 group-hover:text-primary transition-transform duration-150" />
                    <span className={area.label === "View All Areas" ? "font-medium text-foreground group-hover:text-primary" : ""}>
                      {area.label}
                    </span>
                  </Link>
                </li>)}
            </ul>
          </div>

          {/* Column 5: Navigation Menu (2 Cols) */}
          <div className="lg:col-span-2 space-y-3.5">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground font-default border-b border-border/40 pb-2">
              Quick Links
            </h3>
            <MenuProvider menu_id="17">
              <FooterMenuConsumer />
            </MenuProvider>
          </div>

          {/* Column 6: Contact & Coverage (2 Cols) */}
          <div className="lg:col-span-2 space-y-3.5">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-foreground font-default border-b border-border/40 pb-2">
              Operational Contact
            </h3>
            
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex items-start gap-2.5">
                <MapPin className="size-4 text-primary shrink-0 mt-0.5" />
                <span>South East England, covering Berkshire, Surrey, Hampshire, Buckinghamshire & London</span>
              </div>

              <div className="flex items-center gap-2.5">
                <Phone className="size-4 text-primary shrink-0" />
                <a href="tel:01344929900" className="hover:text-primary transition-colors duration-150 text-foreground">
                  01344 929900
                </a>
              </div>

              <div className="flex items-center gap-2.5">
                <Mail className="size-4 text-primary shrink-0" />
                <a href="mailto:quotes@bmfscaffolding.co.uk" className="hover:text-primary transition-colors duration-150 break-all">
                  quotes@bmfscaffolding.co.uk
                </a>
              </div>

              <div className="flex items-center gap-2.5 pt-1">
                <Clock className="size-4 text-primary shrink-0" />
                <span className="text-xs">Monday to Friday, 8am - 6pm</span>
              </div>

              <div className="pt-2 border-t border-border/40">
                <Link to="https://www.google.com/search?q=bmf+scaffolding+ltd&oq=bmf+&gs_lcrp=EgZjaHJvbWUqBggAEEUYOzIGCAAQRRg7MgYIARBFGDsyBggCEEUYOzIKCAMQABixAxiABDIGCAQQRRg5MgYIBRBFGDwyBggGEEUYPTIGCAcQRRg90gEIMTc4NmowajSoAgGwAgHxBROQgNLGWCy_&sourceid=chrome&source=chrome.ob&ie=UTF-8#lrd=0x64b49614cc209c29:0x827d0295d25a089a,1," newTab className="group inline-flex items-center gap-1.5 text-xs font-semibold text-primary hover:text-primary/80 transition-colors duration-150">
                  <span>Leave us a Google Review</span>
                  <ExternalLink className="size-3.5 group-hover:translate-x-0.5 transition-transform duration-150" />
                </Link>
              </div>

              {/* Follow BMF Social Links */}
              <div className="pt-2 border-t border-border/40 space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-foreground">
                  Follow BMF
                </p>
                <div className="flex items-center gap-3 text-muted-foreground">
                  <Link to="https://www.instagram.com/bmfscaffolding/" newTab aria-label="BMF Scaffolding on Instagram" className="hover:text-primary transition-colors duration-150">
                    <Instagram className="size-4" />
                  </Link>

                  <Link to="https://www.facebook.com/profile.php?id=61564739075843" newTab aria-label="BMF Scaffolding on Facebook" className="hover:text-primary transition-colors duration-150">
                    <Facebook className="size-4" />
                  </Link>

                  <Link to="https://www.tiktok.com/@bmfscaffoldingltd" newTab aria-label="BMF Scaffolding on TikTok" className="hover:text-primary transition-colors duration-150">
                    <svg className="size-4 fill-current" viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298-.002.595.042.88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 3 15.68 6.34 6.34 0 0 0 9.33 22a6.33 6.33 0 0 0 6.33-6.32V8.92a8.28 8.28 0 0 0 4.84 1.55V7.07a4.85 4.85 0 0 1-.91-.38Z" />
                    </svg>
                  </Link>

                  <Link to="https://www.linkedin.com/in/peter-wood-61a9503aa/" newTab aria-label="Pete Wood / BMF Scaffolding on LinkedIn" className="hover:text-primary transition-colors duration-150">
                    <Linkedin className="size-4" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Separator className="bg-border/60" />

        {/* Bottom Legal, Copyright & Status Row */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <p>
            {"©"} {currentYear} BMF Scaffolding Ltd. All rights reserved. Registered in England & Wales.
          </p>

          <div className="flex items-center gap-6">
            <Link to="/HealthSafetyAccreditationsPage" className="hover:text-foreground transition-colors duration-150">
              Health & Safety Policy
            </Link>
            <Link to="/AreasWeCoverPage" className="hover:text-foreground transition-colors duration-150">
              Service Area Directory
            </Link>
            <Link to="/ContactRequestQuotePage" className="hover:text-foreground transition-colors duration-150">
              Project Enquiries
            </Link>
          </div>
        </div>
      </div>
    </section>;
}